import { useAuthFetch, useGuardedEffect } from "@p0security/frontend-core";
import type {
  AgenticClient,
  AgenticClientCreateResponse,
  AgenticClientCredentialsListResponse,
  AgenticClientListItem,
  AgenticClientUpdateRequest,
  AgenticClientUpdateResponse,
  AgenticClientsListResponse,
  McpServer,
  McpServersListResponse,
} from "@p0security/integrations-agentic-shared/interface";
import { useCallback, useState } from "react";

import type {
  AgenticClientRegistration,
  AgenticRegisteredClient,
} from "./types";

type McpListState<T> = {
  error?: string;
  items: T[];
  loading: boolean;
  refresh: () => void;
};

const listErrorMessage = (resourceLabel: string, status: number) => {
  if (status === 401) return "Your session has expired. Sign in and try again.";
  if (status === 403) {
    return `You do not have permission to view ${resourceLabel}.`;
  }
  return `${resourceLabel} could not be loaded. Try again.`;
};

const registrationErrorMessage = (
  status: number,
  type: AgenticClientRegistration["type"]
) => {
  if (status === 400) return "Check the registration details and try again.";
  if (status === 401) return "Your session has expired. Sign in and try again.";
  if (status === 403) {
    return "You do not have permission to register clients.";
  }
  if (status === 409 && type === "jwt_bearer") {
    return "That identity is already registered for this provider.";
  }
  return "The client could not be registered. Try again.";
};

const updateErrorMessage = (status: number) => {
  if (status === 400) return "Check the client details and try again.";
  if (status === 401) return "Your session has expired. Sign in and try again.";
  if (status === 403) {
    return "You do not have permission to update clients.";
  }
  if (status === 404) return "This client no longer exists.";
  return "The client could not be updated. Try again.";
};

const fetchErrorMessage = (error: string | undefined) =>
  error === "Unauthorized"
    ? "Your session has expired. Sign in and try again."
    : error;

// Selectors live at module scope so their identities stay stable across
// renders; `useMcpList` re-runs its fetch effect whenever the selector changes.
const clientsFromResponse = (response: AgenticClientsListResponse) =>
  response.clients;

const serversFromResponse = (response: McpServersListResponse) =>
  response.servers;

const credentialsFromResponse = (
  response: AgenticClientCredentialsListResponse
) => response.credentials;

/** Loads the items listed at `path`, labelled `resourceLabel` in errors. */
const useMcpList = <T, ResponseBody>(
  path: string,
  resourceLabel: string,
  itemsFromResponse: (response: ResponseBody) => T[]
): McpListState<T> => {
  const [error, setError] = useState<string>();
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const handleFetchError = useCallback(
    (nextError: string | undefined) => setError(fetchErrorMessage(nextError)),
    []
  );
  const authFetch = useAuthFetch(handleFetchError);
  const handleUnknownError = useCallback(
    (loadError: unknown) => {
      setError(
        loadError instanceof Error
          ? loadError.message
          : `${resourceLabel} could not be loaded.`
      );
    },
    [resourceLabel]
  );

  useGuardedEffect(
    (cancellation) => async () => {
      cancellation.guard(setError)(undefined);
      cancellation.guard(setLoading)(true);
      try {
        const response = await authFetch(path, {
          cancellation,
          method: "GET",
          onNotOk: ({ status }) => {
            // If user doesn't have permission we'll just return empty
            if (status === 403) return;
            cancellation.guard(setError)(
              listErrorMessage(resourceLabel, status)
            );
          },
        });
        if (!response || cancellation.isCancelled) return;

        const payload: ResponseBody = await response.json();
        if (cancellation.isCancelled) return;
        if (response.status === 403) {
          setItems([]);
          return;
        }
        setItems(itemsFromResponse(payload));
      } finally {
        cancellation.guard(setLoading)(false);
      }
    },
    [authFetch, itemsFromResponse, path, refreshIndex, resourceLabel],
    handleUnknownError
  );

  const refresh = useCallback(() => setRefreshIndex((value) => value + 1), []);

  return { error, items, loading, refresh };
};

/** Loads MCP clients registered for the active tenant. */
export const useMcpClients = (): McpListState<AgenticClientListItem> =>
  useMcpList<AgenticClient, AgenticClientsListResponse>(
    "agentic/clients",
    "Clients",
    clientsFromResponse
  );

/** Loads MCP servers made available by the active tenant gateway. */
export const useMcpServers = (): McpListState<McpServer> =>
  useMcpList<McpServer, McpServersListResponse>(
    "agentic/servers",
    "MCP servers",
    serversFromResponse
  );

const credentialsPath = (clientId: string) =>
  `agentic/clients/${encodeURIComponent(clientId)}/credentials`;

/** Loads the upstream MCP server credentials the tenant's gateways hold for
 * a client. */
export const useMcpClientCredentials = (clientId: string) =>
  useMcpList<{ id: string }, AgenticClientCredentialsListResponse>(
    credentialsPath(clientId),
    "OAuth credentials",
    credentialsFromResponse
  );

/** Revokes a client's credential for one MCP server, resolving whether it was
 * revoked. Failures are shown by `useAuthFetch`'s default error message. */
export const useMcpClientCredentialRevoke = () => {
  const authFetch = useAuthFetch();
  return useCallback(
    async (clientId: string, credentialId: string) =>
      Boolean(
        await authFetch(
          `${credentialsPath(clientId)}/${encodeURIComponent(credentialId)}`,
          { method: "DELETE" }
        )
      ),
    [authFetch]
  );
};

type McpClientRegistrationState = {
  clearError: () => void;
  error?: string;
  loading: boolean;
  register: (
    registration: AgenticClientRegistration
  ) => Promise<AgenticRegisteredClient | undefined>;
};

/** Registers an agentic client of either kind and returns what was created. */
export const useMcpClientRegistration = (): McpClientRegistrationState => {
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const handleFetchError = useCallback(
    (nextError: string | undefined) => setError(fetchErrorMessage(nextError)),
    []
  );
  const authFetch = useAuthFetch(handleFetchError, setLoading);

  const clearError = useCallback(() => setError(undefined), []);

  const register = useCallback(
    async (registration: AgenticClientRegistration) => {
      setError(undefined);
      try {
        const response = await authFetch("agentic/clients", {
          json: registration,
          method: "POST",
          onNotOk: ({ status }) =>
            setError(registrationErrorMessage(status, registration.type)),
        });
        if (!response) return undefined;

        const payload: AgenticClientCreateResponse = await response.json();
        const { client } = payload;
        // A federated client has no secret to present; a confidential one is
        // useless without the secret, which is only ever returned here.
        if (client.type === "jwt_bearer") return client;
        if (!client.secret) {
          throw new Error(
            "The registration response did not include credentials."
          );
        }
        return { ...client, secret: client.secret };
      } catch (registrationError: unknown) {
        setError(
          registrationError instanceof Error
            ? registrationError.message
            : "The registration response could not be read."
        );
        return undefined;
      }
    },
    [authFetch]
  );

  return { clearError, error, loading, register };
};

type McpClientUpdateState = {
  clearError: () => void;
  error?: string;
  loading: boolean;
  update: (
    clientId: string,
    metadata: AgenticClientUpdateRequest
  ) => Promise<AgenticClientListItem | undefined>;
};

/** Updates customer-managed metadata for an MCP client. */
export const useMcpClientUpdate = (): McpClientUpdateState => {
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const handleFetchError = useCallback(
    (nextError: string | undefined) => setError(fetchErrorMessage(nextError)),
    []
  );
  const authFetch = useAuthFetch(handleFetchError);

  const clearError = useCallback(() => setError(undefined), []);

  const update = useCallback(
    async (clientId: string, metadata: AgenticClientUpdateRequest) => {
      setError(undefined);
      setLoading(true);
      try {
        const response = await authFetch(
          `agentic/clients/${encodeURIComponent(clientId)}`,
          {
            json: metadata,
            method: "PATCH",
            onNotOk: ({ status }) => setError(updateErrorMessage(status)),
          }
        );
        if (!response) return undefined;

        const payload: AgenticClientUpdateResponse = await response.json();
        return payload.client;
      } catch (updateError: unknown) {
        setError(
          updateError instanceof Error
            ? updateError.message
            : "The update response could not be read."
        );
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [authFetch]
  );

  return { clearError, error, loading, update };
};
